import Sentry from "@similarweb/sentry-logger";

import { toTimeGranularitySymbol } from "app/common/utils/timeGranularity";
import { MARKET_SHARES_METRIC_TYPE } from "app/components/dashboard/widget-wizard/components/MarketAnalysis/constants";
import { DASHBOARD_ID_PREVIEW_MODE } from "app/components/dashboard/widget-wizard/constants";
import { selectCurrentDashboardId } from "app/components/dashboard/widget-wizard/selectors/dashboardSelectors";
import { BarChartTemplateV2 } from "app/components/widget/dashboard/BarChartTemplate-v2";
import MarketAnalysisWidgetFilters from "app/components/widget/widget-filters/MarketAnalysisWidgetFilters";
import MarketAnalysisSubtitle from "app/components/widget/widget-subtitle-templates/MarketAnalysisSubtitle";
import { selectDashboardSegments } from "app/pages/dashboard/redux/selectors";
import type { EnrichedData, TableRawData } from "app/pages/industry-analysis/players/market-shares/types";
import { parseTableRawDataForWidget } from "app/pages/industry-analysis/players/market-shares/utils/parseTableRawData";
import {
    CHART_DISPLAY_TYPE_MD,
    chartDisplayTypeFromDataMode,
    dashboardDataDisplayType,
    dataDisplayTypeForUrl,
} from "app/pages/industry-analysis/players/market-shares/utils/utils";
import { loadCustomSegmentsMetadataForDashboard } from "app/pages/segments/config/segmentsConfigHelpers";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";

import type { LegendPropsTypes } from "../../widget-utilities/segments/LegendItems";
import { LegendItems } from "../../widget-utilities/segments/LegendItems";
import { ChartWidget } from "../ChartWidget";
import { WidgetMessages, WidgetState } from "../Widget";

import { getBarChartConfig } from "./configs";
import {
    createLegendsItems,
    filterResponseBySelectedKeys,
    getApiParams,
    getIndustrieById,
    resolveMarketSharesApiTimeGranularity,
} from "./helpers";

const ENDPOINT = "api/Market/Players/Table";
const MIN_ITEMS_TO_SHOW_OTHERS = 5;
type MarketSharesDataMode = keyof typeof chartDisplayTypeFromDataMode;

export abstract class MarketSharesWidget extends ChartWidget {
    public metric: string;
    reactLegendComponent: ({ legendItems, colors }: LegendPropsTypes) => React.ReactNode;

    constructor() {
        super();

        this.metric = MARKET_SHARES_METRIC_TYPE;
    }

    private get resolvedDataMode(): MarketSharesDataMode | undefined {
        const state = this._reduxService.getState();
        const persistedDataMode = this._widgetConfig.properties.dataMode as MarketSharesDataMode | undefined;
        const wizardDataMode = state.customDashboard.dashboardWizard.widget?.dataMode as
            | MarketSharesDataMode
            | undefined;

        return wizardDataMode || persistedDataMode;
    }

    private get resolvedChartDisplayType() {
        const selectedDataMode = this.resolvedDataMode;

        return selectedDataMode ? chartDisplayTypeFromDataMode[selectedDataMode] : this.apiParams.chartDisplayType;
    }

    private get resolvedChartDisplayTypeForUrl() {
        const selectedDataMode = this.resolvedDataMode;
        const dataMode = selectedDataMode ?? dashboardDataDisplayType[this.apiParams.chartDisplayType];

        return dataDisplayTypeForUrl[dataMode];
    }

    static getWidgetMetadataType() {
        return MARKET_SHARES_METRIC_TYPE;
    }

    static getWidgetDashboardType() {
        return "BarChart";
    }

    get templateComponent() {
        return BarChartTemplateV2;
    }

    static getFiltersComponent() {
        return MarketAnalysisWidgetFilters;
    }

    public callbackOnGetData() {
        const compareKeys = this._params.keys.split(",");
        this.metadata.chartOptions.isCompare = compareKeys.length > 1;
        this.metadata.chartOptions.viewOptions = this._viewOptions;
    }

    public getViewData() {
        super.getViewData();
        const { forWidget } = DurationService.getDurationData(
            this._widgetConfig.properties.duration,
            this._widgetConfig.properties.comparedDuration,
            this._metricConfig.component,
        );

        this.viewData.customSubtitle = "true";
        this.viewData.customSubtitleComponent = MarketAnalysisSubtitle;
        this.viewData.duration = forWidget;
        this.viewData.isDashboard = this._swNavigator.isDashboard();

        this.emit(WidgetMessages.WIDGET_UPDATED, this.viewData);

        return this.viewData;
    }

    protected setMetadata() {
        const widgetProp = this._widgetConfig.properties;
        const chartOptions = {
            type: "BarChart",
            height: widgetProp.height,
            dashboardId: this.dashboardId,
        };
        this.metadata = { chartOptions };
    }

    public isPptSupported = () => {
        return false;
    };

    getDataMetric = async () => {
        const fetchService = DefaultFetchService.getInstance();
        const state = this._reduxService.getState();

        const { filters } = state.customDashboard.dashboardWizard.widget;
        const duration = this.durationObject.forAPI;
        const { country } = this._params;

        const widgetCountry = Number(country);

        if (widgetCountry !== this.apiParams.country) {
            this.apiParams.country = widgetCountry;
        }

        const apiParams = getApiParams(this.apiParams, duration, filters, MARKET_SHARES_METRIC_TYPE);

        const res: TableRawData = await fetchService.get(ENDPOINT, apiParams);
        const propKeys = this._widgetConfig.properties.key;
        const keys =
            propKeys.length === 0
                ? res.Data.sort((a, b) => b.Share - a.Share)
                      .slice(0, MIN_ITEMS_TO_SHOW_OTHERS)
                      .map((item) => ({
                          id: item.Id,
                          visible: true,
                          domain: item.Domain,
                          image: item.Favicon,
                          type: item.Type,
                      }))
                : propKeys;

        const dashboardSegments = selectDashboardSegments(state);

        if (!dashboardSegments && this.widgetConfig?.includesSegments) {
            const id =
                this.dashboardId === DASHBOARD_ID_PREVIEW_MODE ? selectCurrentDashboardId(state) : this.dashboardId;

            await loadCustomSegmentsMetadataForDashboard(id);
        }

        const filteredData: TableRawData = filterResponseBySelectedKeys(res.Data, keys);

        return parseTableRawDataForWidget(filteredData, keys);
    };

    initChart(data: EnrichedData) {
        const chartDisplayType = this.resolvedChartDisplayType;

        const { formatter, averageValueMetric } = CHART_DISPLAY_TYPE_MD[chartDisplayType];

        const { finalConfig, dataWithOthers } = getBarChartConfig(
            data,
            averageValueMetric,
            chartDisplayType,
            formatter,
            this.durationObject.forWidget,
        );

        this.chartConfig = {
            options: finalConfig,
            series: [{ data: dataWithOthers }],
        };

        this.legendItems = createLegendsItems(this.chartConfig.series[0].data);

        this.reactLegendComponent = LegendItems;

        this.widgetState = WidgetState.LOADED;
    }

    async getData() {
        const state = this._reduxService.getState();
        let isEnabled = true;

        if (this.id) {
            const segmentWidgetAvailability = state.segmentsModule?.segmentWidgetAvailability?.[this.id];

            if (segmentWidgetAvailability) {
                isEnabled = segmentWidgetAvailability?.isEnabled;
            }
        }

        if (!isEnabled) {
            this.widgetState = WidgetState.SOFT_LIMIT_REACHED;

            return;
        }

        try {
            const response = await this.getDataMetric();
            this.originalData = response;

            if (response) {
                this.initChart(response);
            } else {
                this.widgetState = WidgetState.ERROR;
            }

            this.notifyWidgetUpdated();
        } catch (error) {
            Sentry.errorTracker({
                message: "MarketSharesWidget: Error",
                e: error,
                level: "error",
                params: {
                    extra: {
                        metric: this.metric,
                    },
                },
            });
            this.widgetState = WidgetState.ERROR;
        }
    }

    public getProUrl() {
        const { webSource, country, duration, filters } = this._widgetConfig?.properties;
        const industry = getIndustrieById(filters.category || this.apiParams.category);

        const state = this._metricConfig.state;
        const chartDisplayType = this.resolvedChartDisplayTypeForUrl;

        const durationForApi = DurationService.getDurationData(
            duration,
            this._widgetConfig.properties.comparedDuration,
            this._metricConfig.component,
        ).forAPI;

        const result = {
            duration,
            country,
            webSource,
            category: industry?.forUrl,
            marketSharesDisplayType: chartDisplayType,
            marketSharesChartType: "AVERAGE",
            marketSharesTimeGranularity: toTimeGranularitySymbol(
                resolveMarketSharesApiTimeGranularity(filters, durationForApi.isWindow, MARKET_SHARES_METRIC_TYPE),
            ),
        };

        if (typeof state === "undefined") {
            return this._swNavigator.href("digitalsuite_webmarketanalysis_home", {}, {});
        }
        const url = this._swNavigator.href(state, result, {});

        return url;
    }
}

MarketSharesWidget.register();
